import type { CodigoError, RespuestaSesion } from '@copathy/shared';

/**
 * Cliente HTTP del proyecto.
 *
 * El access token vive **en memoria**, nunca en `localStorage`: ahí lo puede
 * leer cualquier script inyectado. El refresh vive en una cookie `httpOnly` que
 * pone el servidor y que el navegador manda solo — el JavaScript de esta app
 * ni siquiera la puede ver, que es justamente el punto.
 */
let accessToken: string | null = null;

export function fijarAccessToken(token: string | null): void {
  accessToken = token;
}

export function hayAccessToken(): boolean {
  return accessToken !== null;
}

export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    readonly codigo: CodigoError,
    mensaje: string,
    readonly campos?: Record<string, string>,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }
}

type CuerpoError = {
  error?: { codigo?: CodigoError; mensaje?: string; campos?: Record<string, string> };
};

async function aError(respuesta: Response): Promise<ErrorApi> {
  let cuerpo: CuerpoError = {};
  try {
    cuerpo = (await respuesta.json()) as CuerpoError;
  } catch {
    // Una respuesta sin JSON (un 502 de nginx, por ejemplo) no debe romper acá.
  }
  return new ErrorApi(
    respuesta.status,
    cuerpo.error?.codigo ?? 'error_interno',
    cuerpo.error?.mensaje ?? 'No pudimos completar la operación. Intenta de nuevo.',
    cuerpo.error?.campos,
  );
}

async function ejecutar(ruta: string, opciones: RequestInit): Promise<Response> {
  const cabeceras = new Headers(opciones.headers);
  /*
    ⚠️ `FormData` se excluye a propósito. `multipart/form-data` lleva un `boundary`
    que genera el navegador, y ponerle `Content-Type` a mano —aunque sea el
    correcto— lo deja SIN ese boundary: el servidor recibe un cuerpo que no sabe
    separar y responde «no llegó ningún archivo». El error apunta al archivo y la
    causa está acá.
  */
  const esFormulario = opciones.body instanceof FormData;
  if (!cabeceras.has('Content-Type') && opciones.body && !esFormulario) {
    cabeceras.set('Content-Type', 'application/json');
  }
  if (accessToken) cabeceras.set('Authorization', `Bearer ${accessToken}`);

  return fetch(ruta, {
    ...opciones,
    headers: cabeceras,
    // Necesario para que viaje la cookie del refresh.
    credentials: 'same-origin',
  });
}

/**
 * Renovación de sesión.
 *
 * Se guarda la promesa en curso para que varias peticiones que fallan a la vez
 * no disparen cuatro refresh en paralelo: el token rota en cada uso, así que
 * dos refresh simultáneos harían que el segundo llegue con un token ya rotado
 * — y el servidor lo interpreta, con razón, como un token robado y cierra la
 * sesión entera.
 */
let renovacionEnCurso: Promise<boolean> | null = null;

async function renovar(): Promise<boolean> {
  renovacionEnCurso ??= (async () => {
    try {
      const r = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
        credentials: 'same-origin',
      });
      if (!r.ok) return false;
      const datos = (await r.json()) as RespuestaSesion;
      accessToken = datos.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Se libera en el siguiente turno para que las peticiones que estaban
      // esperando lean el resultado antes de que se limpie.
      setTimeout(() => {
        renovacionEnCurso = null;
      }, 0);
    }
  })();

  return renovacionEnCurso;
}

async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  let respuesta = await ejecutar(ruta, opciones);

  // Un 401 puede ser «el access token venció». Se intenta renovar UNA vez.
  if (respuesta.status === 401 && !ruta.includes('/auth/login')) {
    const renovado = await renovar();
    if (renovado) {
      respuesta = await ejecutar(ruta, opciones);
    }
  }

  if (!respuesta.ok) throw await aError(respuesta);
  if (respuesta.status === 204) return undefined as T;
  return (await respuesta.json()) as T;
}

/**
 * Descarga un binario protegido —hoy, el logotipo de un cliente—.
 *
 * Va aparte de `pedir` porque no hay JSON que interpretar y porque un 404 acá es
 * una respuesta legítima («no tiene logo»), no un error que valga la pena
 * propagar con mensaje. El 401 sí pasa por la misma renovación que el resto: sin
 * eso, las imágenes se caerían solas cada quince minutos.
 */
export async function apiBlob(ruta: string): Promise<Blob> {
  let respuesta = await ejecutar(ruta, {});
  if (respuesta.status === 401 && (await renovar())) {
    respuesta = await ejecutar(ruta, {});
  }
  if (!respuesta.ok) throw await aError(respuesta);
  return respuesta.blob();
}

/**
 * Sube un archivo. **Sin `Content-Type` a mano**: el navegador tiene que ponerlo
 * él, porque `multipart/form-data` lleva un `boundary` que solo él conoce. Fijarlo
 * a mano produce un cuerpo que el servidor no sabe separar, y el error que se ve
 * es «no llegó ningún archivo».
 */
export async function apiSubir<T>(ruta: string, campo: string, archivo: File): Promise<T> {
  const cuerpo = new FormData();
  cuerpo.append(campo, archivo);
  return pedir<T>(ruta, { method: 'PUT', body: cuerpo });
}

export const api = {
  get: <T>(ruta: string) => pedir<T>(ruta),
  delete: <T>(ruta: string) => pedir<T>(ruta, { method: 'DELETE' }),
  post: <T>(ruta: string, cuerpo?: unknown) =>
    pedir<T>(ruta, { method: 'POST', body: JSON.stringify(cuerpo ?? {}) }),
  put: <T>(ruta: string, cuerpo?: unknown) =>
    pedir<T>(ruta, { method: 'PUT', body: JSON.stringify(cuerpo ?? {}) }),
  patch: <T>(ruta: string, cuerpo?: unknown) =>
    pedir<T>(ruta, { method: 'PATCH', body: JSON.stringify(cuerpo ?? {}) }),
  renovar,
};
